import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';
import useUserPermissions from '../hooks/useUserPermissions';

function EditModule() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canModify, isLoading } = useUserPermissions();

  const [moduleId, setModuleId] = useState('');
  const [modifiedBy, setModifiedBy] = useState('');
  const [moduleDescription, setModuleDescription] = useState('');
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect users without modify permissions
  useEffect(() => {
    if (!isLoading && !canModify()) {
      alert('You do not have permission to edit modules. Redirecting to view mode.');
      navigate(`/view-module/${id}`);
      return;
    }
  }, [canModify, isLoading, navigate, id]);

  useEffect(() => {
    const fetchModuleData = async () => {
      try {
        const data = await apiCall(`/modules/${id}`);
        setModuleId(data["Module ID"]);
        setModifiedBy(data["ModifiedBy"]);
        setModuleDescription(data["Module Description"]);
        // Ensure steps is always an array
        setSteps(data["Steps"] || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchModuleData();
  }, [id]);

  const handleStepChange = (index, field, value) => {
    const updatedSteps = steps.map((step, i) =>
      i === index ? { ...step, [field]: value } : step
    );
    setSteps(updatedSteps);
  };
  
  // Add/Delete step handlers are the same as in CreateModule
  const handleAddStep = () => setSteps(prev => [...prev, { "Step Number": prev.length + 1, "Description": '', "URL": '' }]);
  const handleRemoveStep = (index) => {
      const renumbered = steps.filter((_, i) => i !== index).map((s, i) => ({...s, "Step Number": i + 1}));
      setSteps(renumbered);
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      moduleDescription,
      modifiedBy,
      steps,
    };

    try {
      await apiCall(`/modules/${moduleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      alert('Module updated successfully!');
      navigate('/module_list');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to update module.');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container mt-4">
      {/* The form structure is very similar to CreateModule */}
      <form onSubmit={handleSubmit}>
        <h2>Edit Module</h2>
        <label>Module ID:</label>
        <input type="text" value={moduleId} readOnly className="form-control"/>

        <label>Module Description:</label>
        <input type="text" value={moduleDescription} onChange={(e) => setModuleDescription(e.target.value)} required className="form-control"/>

        <h3>Steps</h3>
        <table className="table table-bordered">
            <thead><tr><th>#</th><th>Description</th><th>URL</th><th>Action</th></tr></thead>
            <tbody>
                {steps.map((step, index) => (
                    <tr key={index}>
                        <td><input type="text" value={step['Step Number']} readOnly className="form-control" /></td>
                        <td><input type="text" value={step.Description} onChange={(e) => handleStepChange(index, 'Description', e.target.value)} required className="form-control" /></td>
                        <td><input type="url" value={step.URL} onChange={(e) => handleStepChange(index, 'URL', e.target.value)} className="form-control" /></td>
                        <td><button type="button" onClick={() => handleRemoveStep(index)} className="btn btn-danger btn-sm">Remove</button></td>
                    </tr>
                ))}
            </tbody>
        </table>
        <button type="button" onClick={handleAddStep} className="btn btn-success">+ Add Step</button>
        
        <br/><br/>
        <label>Modified By:</label>
        <input type="text" value={modifiedBy} onChange={(e) => setModifiedBy(e.target.value)} required className="form-control"/>
        
        <div className="d-flex justify-content-end gap-2 mt-3">
            <button type="submit" className="btn btn-primary">Save Changes</button>
            <button type="button" onClick={() => navigate('/module_list')} className="btn btn-secondary mx-2">Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default EditModule;
